import {
  canonicalizePaymentFailureCode,
  paymentFailureI18nKey,
  type PaymentFailureCode,
} from './codes'
import { PROVIDER_FAILURE_MATCHERS } from './adapters/failure-matchers'

export type ClassifiedPaymentFailure = {
  code: PaymentFailureCode
  i18nKey: string
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function looksLikeMarkup(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text)
}

export function classifyPaymentFailure(input: {
  code?: string | null
  reason?: string | null
  message?: string | null
}): ClassifiedPaymentFailure {
  const fromCode = canonicalizePaymentFailureCode(input.code)
  if (fromCode !== 'UNKNOWN') {
    return { code: fromCode, i18nKey: paymentFailureI18nKey(fromCode) }
  }

  const blob = firstNonEmpty(input.reason, input.message)
  if (blob && !looksLikeMarkup(blob)) {
    for (const matcher of PROVIDER_FAILURE_MATCHERS) {
      if (matcher.test(blob)) {
        return { code: matcher.code, i18nKey: paymentFailureI18nKey(matcher.code) }
      }
    }
  }

  return { code: 'UNKNOWN', i18nKey: paymentFailureI18nKey('UNKNOWN') }
}

/**
 * Copy for a toast description. Known codes become i18n. An unknown but
 * human reason is kept (the bank said something true). Markup is never
 * shown — that was the raw threeds-return dump.
 */
export function paymentFailureDescription(
  input: {
    code?: string | null
    reason?: string | null
    message?: string | null
  },
  t: (key: string) => string,
  fallback: string,
): string {
  const classified = classifyPaymentFailure(input)
  if (classified.code !== 'UNKNOWN') return t(classified.i18nKey)

  const reason = typeof input.reason === 'string' ? input.reason.trim() : ''
  if (reason && reason !== fallback && !looksLikeMarkup(reason)) return reason
  return fallback
}

export function isPaymentFunctionBootFailure(message: string): boolean {
  return classifyPaymentFailure({ message }).code === 'CREATE_FAILED'
}
